import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RtiExtensionRequestModal from '../../components/tasks/RtiExtensionRequestModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import * as commService from '../../services/caseRequestCommunications';
import { requestRightToInformationExtension } from '../../services/rightToInformationCaseRequests';
import type { CaseRequestCommunicationMessage } from '../../types/caseRequestCommunications';
import { formatCommunicationTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { isInProgressTaskStatus, isOnHoldTaskStatus } from '../../utils/taskCaseDetailMappers';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';

const MAX_COMMENT_LENGTH = 2000;
const COMMENT_PATTERN = /^[\p{L}\p{N}\s.,!?'"()\-:;/@#&%]*$/u;

type Props = {
  caseRequestId: string;
  taskStatus: string;
  /** workflow_instance_stage_user_task_id — required for RTI extension request (CMS-CRRTI-3). */
  workflowInstanceStageUserTaskId?: string;
  /** When true, show RTI Request Extension / Resume Upon Extension Response instead of generic hold. */
  isRtiCase?: boolean;
  /** View screen / rejected — entire tab is read-only. */
  readOnly?: boolean;
  /** Add-a-comment fields — enabled only when status is In Progress. */
  composeEnabled?: boolean;
  /** On Hold / Resume actions — enabled in edit mode when In Progress or On Hold. */
  holdResumeEnabled?: boolean;
  onTaskStatusChange?: () => void;
};

type CommRow = {
  id: string;
  senderLabel: string;
  time: string;
  body: string;
  visibleToExternalAgency: boolean;
};

function HoldStatusPauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

function HoldStatusResumeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function senderDisplayName(msg: CaseRequestCommunicationMessage): string {
  return msg.senderSubtitle.trim() || msg.senderName.trim() || '—';
}

function senderLabel(msg: CaseRequestCommunicationMessage): string {
  const name = senderDisplayName(msg);
  if (msg.intranet) {
    const role = msg.role.trim();
    return role ? `${name} - ${role}` : name;
  }
  const agency = msg.agency.trim();
  return agency ? `${name} - ${agency}` : name;
}

function mapApiMessage(msg: CaseRequestCommunicationMessage): CommRow {
  return {
    id: msg.id,
    senderLabel: senderLabel(msg),
    time: formatCommunicationTime(msg.createdAt),
    body: msg.comment,
    visibleToExternalAgency: msg.visibleToExternalAgency,
  };
}

function latestVisibleExternalMessage(messages: CommRow[]): CommRow | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].visibleToExternalAgency) return messages[i];
  }
  return null;
}

export default function TaskCaseCommunicationsTab({
  caseRequestId,
  taskStatus,
  workflowInstanceStageUserTaskId = '',
  isRtiCase = false,
  readOnly = false,
  composeEnabled = false,
  holdResumeEnabled = false,
  onTaskStatusChange,
}: Props) {
  const { t } = useTranslation();
  const canLoad = isCaseRequestUuid(caseRequestId);
  const taskInProgress = isInProgressTaskStatus(taskStatus);
  const taskOnHold = isOnHoldTaskStatus(taskStatus);
  const canCompose = !readOnly && composeEnabled;
  const validUserTaskId = isCaseRequestUuid(workflowInstanceStageUserTaskId);

  const [loading, setLoading] = useState(canLoad);
  const [loadError, setLoadError] = useState('');
  const [messages, setMessages] = useState<CommRow[]>([]);
  const [comment, setComment] = useState('');
  const [visibleExternal, setVisibleExternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [holdResumeBusy, setHoldResumeBusy] = useState(false);
  const [extensionModalOpen, setExtensionModalOpen] = useState(false);
  const [extensionSubmitting, setExtensionSubmitting] = useState(false);
  /** Message id from the latest comment added with Visible to External Agency checked. */
  const [holdEligibleMessageId, setHoldEligibleMessageId] = useState<string | null>(null);
  /** True after a successful Hold until Resume succeeds or parent reloads with In Progress. */
  const [awaitingResume, setAwaitingResume] = useState(false);
  const [resumeAcknowledged, setResumeAcknowledged] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [visibleExternalWarningOpen, setVisibleExternalWarningOpen] = useState(false);

  const holdEligibleMessage = useMemo(
    () => messages.find((m) => m.id === holdEligibleMessageId) ?? null,
    [holdEligibleMessageId, messages],
  );
  const holdContextMessage = useMemo(() => {
    if (holdEligibleMessage) return holdEligibleMessage;
    if (taskOnHold || awaitingResume) return latestVisibleExternalMessage(messages);
    return null;
  }, [awaitingResume, holdEligibleMessage, messages, taskOnHold]);

  const wasOnHoldRef = useRef(taskOnHold);

  useEffect(() => {
    if (taskOnHold) {
      setAwaitingResume(true);
      setResumeAcknowledged(false);
    } else if (wasOnHoldRef.current && taskInProgress) {
      setAwaitingResume(false);
      setHoldEligibleMessageId(null);
      setResumeAcknowledged(true);
    }
    wasOnHoldRef.current = taskOnHold;
  }, [taskOnHold, taskInProgress]);

  const showGenericOnHold =
    !isRtiCase &&
    holdResumeEnabled &&
    !readOnly &&
    taskInProgress &&
    !taskOnHold &&
    !awaitingResume &&
    holdEligibleMessage != null &&
    holdEligibleMessage.visibleToExternalAgency;
  const showGenericResume =
    !isRtiCase &&
    holdResumeEnabled &&
    !readOnly &&
    (taskOnHold || awaitingResume) &&
    !resumeAcknowledged &&
    holdContextMessage != null;
  const showRtiRequestExtension =
    isRtiCase &&
    holdResumeEnabled &&
    !readOnly &&
    taskInProgress &&
    !taskOnHold &&
    validUserTaskId;
  const showRtiResumeExtension =
    isRtiCase &&
    holdResumeEnabled &&
    !readOnly &&
    taskOnHold &&
    holdContextMessage != null;

  const requestHold = () => {
    if (
      !holdEligibleMessage ||
      !holdEligibleMessage.visibleToExternalAgency ||
      holdEligibleMessage.id !== holdEligibleMessageId
    ) {
      setVisibleExternalWarningOpen(true);
      return;
    }
    void handleHoldStatus('HOLD');
  };

  const loadThread = useCallback(async (): Promise<CommRow[]> => {
    if (!canLoad) {
      setLoading(false);
      setLoadError(t('tasks.details.communications.invalidCaseRequest'));
      return [];
    }

    setLoading(true);
    setLoadError('');
    const r = await commService.getCaseRequestCommunications(caseRequestId);
    setLoading(false);
    if (!r.success) {
      const msg = r.message ?? t('tasks.details.communications.loadFailed');
      setLoadError(msg);
      setFlashToast({ message: msg, variant: 'error' });
      return [];
    }
    const rows = r.data.messages.map(mapApiMessage);
    setMessages(rows);
    return rows;
  }, [canLoad, caseRequestId, t]);

  useEffect(() => {
    void loadThread();
  }, [loadThread]);

  const submitComment = async () => {
    const text = comment.trim();
    if (!text) {
      setFlashToast({ message: t('tasks.details.communications.commentRequired'), variant: 'error' });
      return;
    }
    if (text.length > MAX_COMMENT_LENGTH) {
      setFlashToast({
        message: t('tasks.details.communications.commentTooLong', { max: MAX_COMMENT_LENGTH }),
        variant: 'error',
      });
      return;
    }
    if (!COMMENT_PATTERN.test(text)) {
      setFlashToast({ message: t('tasks.details.communications.commentInvalid'), variant: 'error' });
      return;
    }
    if (submitting || !canLoad || !canCompose) return;

    setSubmitting(true);
    const wasVisibleExternal = visibleExternal;
    const r = await commService.addCaseRequestComment(caseRequestId, text, {
      visibleToExternalAgency: visibleExternal,
    });
    setSubmitting(false);

    if (!r.success) {
      setFlashToast({ message: r.message ?? t('tasks.details.communications.addFailed'), variant: 'error' });
      return;
    }

    setComment('');
    setVisibleExternal(false);
    setFlashToast({ message: t('tasks.details.communications.addSuccess'), variant: 'success' });
    const rows = await loadThread();
    if (wasVisibleExternal && taskInProgress) {
      const byId = rows.find((m) => m.id === r.data.id);
      const target = byId ?? latestVisibleExternalMessage(rows);
      if (target) setHoldEligibleMessageId(target.id);
    }
  };

  const handleHoldStatus = async (action: 'HOLD' | 'RESUME') => {
    if (holdResumeBusy) return;
    if (!holdContextMessage) return;

    setHoldResumeBusy(true);
    const res = await commService.updateCommunicationHoldStatus(caseRequestId, {
      action,
      visibleToExternalAgency: holdContextMessage.visibleToExternalAgency,
    });
    setHoldResumeBusy(false);

    if (!res.success) {
      const message =
        action === 'HOLD'
          ? (res.message ?? t('tasks.details.communications.holdFailed'))
          : (res.message ?? t('tasks.details.communications.resumeFailed'));
      setFlashToast({ message, variant: 'error' });
      return;
    }

    if (action === 'HOLD') {
      setAwaitingResume(true);
      setFlashToast({ message: t('tasks.details.communications.holdSuccess'), variant: 'success' });
    } else {
      setAwaitingResume(false);
      setResumeAcknowledged(true);
      setHoldEligibleMessageId(null);
      setFlashToast({ message: t('tasks.details.communications.resumeSuccess'), variant: 'success' });
    }
    onTaskStatusChange?.();
  };

  const submitExtensionRequest = async (reason: string) => {
    if (extensionSubmitting || !validUserTaskId) return;

    setExtensionSubmitting(true);
    const res = await requestRightToInformationExtension({
      caseRequestId,
      workflowInstanceStageUserTaskId,
      description: reason,
    });
    setExtensionSubmitting(false);

    if (!res.success) {
      setFlashToast({
        message: res.message ?? t('tasks.details.communications.rtiExtension.requestFailed'),
        variant: 'error',
      });
      return;
    }

    setExtensionModalOpen(false);
    setAwaitingResume(true);
    setFlashToast({
      message: t('tasks.details.communications.rtiExtension.requestSuccess'),
      variant: 'success',
    });
    await loadThread();
    onTaskStatusChange?.();
  };

  if (!canLoad) {
    return <p className="tcd-doc-empty-msg">{t('tasks.details.communications.invalidCaseRequest')}</p>;
  }

  if (loading) {
    return <p className="tcd-loading">{t('tasks.details.communications.loading')}</p>;
  }

  if (loadError) {
    return (
      <p className="tcd-error" role="alert">
        {loadError}
      </p>
    );
  }

  const handleCancelClick = () => {
    if (comment.trim()) {
      setCancelConfirmOpen(true);
      return;
    }
    setComment('');
    setVisibleExternal(false);
  };

  const showActionBar =
    showGenericOnHold || showGenericResume || showRtiRequestExtension || showRtiResumeExtension;

  return (
    <>
      <RtiExtensionRequestModal
        open={extensionModalOpen}
        busy={extensionSubmitting}
        onClose={() => setExtensionModalOpen(false)}
        onSubmit={(reason) => void submitExtensionRequest(reason)}
      />

      <ConfirmDialog
        isOpen={visibleExternalWarningOpen}
        title={t('tasks.details.communications.visibleExternalWarningTitle')}
        message={t('tasks.details.communications.visibleExternalWarningMessage')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.close')}
        onClose={() => setVisibleExternalWarningOpen(false)}
        onConfirm={() => setVisibleExternalWarningOpen(false)}
      />

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('tasks.details.communications.cancelTitle')}
        message={t('tasks.details.communications.cancelMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setComment('');
          setVisibleExternal(false);
          setCancelConfirmOpen(false);
        }}
      />

      <div className="tcd-comm">
        <h3 className="tcd-comm-title">{t('tasks.details.tabCommunications')}</h3>

        <div className="tcd-comm-thread">
          {messages.length ? (
            messages.map((msg) => (
              <div key={msg.id} className="tcd-comm-msg">
                <div className="tcd-comm-msg-hdr">
                  <span className="tcd-comm-sender">{msg.senderLabel}</span>
                  <span className="tcd-comm-time">{msg.time}</span>
                </div>
                <p className="tcd-comm-body">{msg.body}</p>
              </div>
            ))
          ) : (
            <p style={{ fontSize: 12, color: 'var(--t3)', margin: 0 }}>{t('common.noDataFound')}</p>
          )}
        </div>

        {showActionBar ? (
          <div
            className={
              showRtiRequestExtension || showRtiResumeExtension
                ? 'tcd-comm-actions tcd-comm-actions--extension-bar'
                : 'tcd-comm-hold-bar'
            }
          >
            {showGenericOnHold ? (
              <button
                type="button"
                className="tcd-comm-hold-btn tcd-comm-hold-btn--hold"
                disabled={holdResumeBusy}
                onClick={requestHold}
              >
                <HoldStatusPauseIcon />
                {holdResumeBusy ? t('common.loading') : t('tasks.details.communications.onHold')}
              </button>
            ) : null}
            {showGenericResume ? (
              <button
                type="button"
                className="tcd-comm-hold-btn tcd-comm-hold-btn--resume"
                disabled={holdResumeBusy}
                onClick={() => void handleHoldStatus('RESUME')}
              >
                <HoldStatusResumeIcon />
                {holdResumeBusy ? t('common.loading') : t('tasks.details.communications.resume')}
              </button>
            ) : null}
            {showRtiRequestExtension ? (
              <button
                type="button"
                className="btn-ca"
                disabled={extensionSubmitting}
                onClick={() => setExtensionModalOpen(true)}
              >
                {extensionSubmitting
                  ? t('common.loading')
                  : t('tasks.details.communications.rtiExtension.requestButton')}
              </button>
            ) : null}
            {showRtiResumeExtension ? (
              <button
                type="button"
                className="btn-ca"
                disabled={holdResumeBusy}
                onClick={() => void handleHoldStatus('RESUME')}
              >
                {holdResumeBusy
                  ? t('common.loading')
                  : t('tasks.details.communications.rtiExtension.resumeButton')}
              </button>
            ) : null}
          </div>
        ) : null}

        {canCompose ? (
          <>
            <div className="tcd-comm-add-hdr">
              <h4 className="tcd-comm-add-title">{t('tasks.details.communications.addComment')}</h4>
              <label className="tcd-comm-vis tcd-comm-vis--inline">
                <input
                  type="checkbox"
                  checked={visibleExternal}
                  disabled={submitting}
                  onChange={(e) => setVisibleExternal(e.target.checked)}
                />
                {t('tasks.details.communications.visibleToExternal')}
              </label>
            </div>
            <textarea
              className="note-ta"
              rows={5}
              placeholder={t('tasks.details.communications.addCommentPlaceholder')}
              value={comment}
              disabled={submitting}
              maxLength={MAX_COMMENT_LENGTH}
              onChange={(e) => setComment(e.target.value)}
            />
            <p className="tcd-comm-hint">
              {comment.length}/{MAX_COMMENT_LENGTH} {t('tasks.details.communications.characters')}
            </p>
            <div className="tcd-comm-actions">
              <button type="button" className="btn-cn2" disabled={submitting} onClick={handleCancelClick}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn-ca"
                disabled={submitting || !comment.trim()}
                onClick={() => void submitComment()}
              >
                {submitting ? t('tasks.details.communications.adding') : t('tasks.details.communications.addComment')}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
